import { application } from 'src';
import { Controller } from '@hotwired/stimulus';
import { findAll, find, getParent, addClass } from '@fretadao/f-js-dom';

application.register(
  'move-passengers',
  class extends Controller {
    static targets = [
      'employeesCheckboxes',
      'passengersCheckboxes',
      'passengersBox',
      'selectedPassengersBox',
      'formFilter',
      'btnApplyFilter',
      'btnUnselectAll',
      'selectedCounter'
    ];

    LIMIT_OF_PASSENGERS = 250;

    connect() {
      this.#reloadEmployeesListAndUpdateCounter();
      this.#addEventOnPassengers();

      document.addEventListener('ajax:success', _event => {
        this.#addEventOnEmployees();
        this.#removeBlockDivOverList();
      });
    }

    disconnect() {
      this.#removeEventOnEmployees();
      this.#removeEventOnPassengers();
    }

    selectAll() {
      const checkboxes = findAll('tbody tr input[type=checkbox]', this.passengersBoxTarget);

      if (this.#countSelectedPassengers() < this.LIMIT_OF_PASSENGERS) {
        this.#notify('Passageiros adicionados', null, 'info');
      } else {
        this.#notifyLimitMessage();
      }

      checkboxes.forEach(checkbox => {
        this.#moveLineToSelectedBox(checkbox);
      });

      this.#reloadEmployeesListAndUpdateCounter();

      this.passengersBoxTarget.scrollIntoView({ block: 'start' });
    }

    unselectAll(event) {
      const checkboxes = findAll('tbody tr input[type=checkbox]', this.selectedPassengersBoxTarget);

      checkboxes.forEach(checkbox => {
        this.#removeLineFromSelectedBox(checkbox);
      });

      this.#notify('Passageiros removidos', null, 'warn');

      this.#reloadEmployeesListAndUpdateCounter();
    }

    #moveLineToSelectedBoxAndReload(event) {
      const passengerMoved = this.#moveLineToSelectedBox(event.target);
      if (passengerMoved) {
        this.#notifySelectedPassenger(event);
      } else {
        this.#notifyLimitMessage();
      }
      this.#reloadEmployeesListAndUpdateCounter();
    }

    #moveLineToSelectedBox(field) {
      // the max number of selected passengers, to not break the limit of query string params
      if (this.#countSelectedPassengers() >= this.LIMIT_OF_PASSENGERS) return;

      const tbodyDesktop = find('#desktop-table tbody', this.selectedPassengersBoxTarget);
      const tbodyMobile = find('#mobile-table', this.selectedPassengersBoxTarget);
      let desktopField, mobileField;

      if(getParent(field, 4).id === 'desktop-table') {
        desktopField = field;
        mobileField = find(`#mobile-table #${field.id}`, this.passengersBoxTarget);
      } else {
        desktopField = find(`#desktop-table #${field.id}`, this.passengersBoxTarget);
        mobileField = field;
      }

      const desktopLine = getParent(desktopField, 2);
      const mobileLine = getParent(mobileField, 3);

      tbodyDesktop.append(desktopLine);
      tbodyMobile.append(mobileLine);

      const passengerName = this.#passengerNameByLine(desktopLine);

      findAll(`#${field.id}`, this.selectedPassengersBoxTarget).forEach(field => {
        field.replaceWith(this.#newCheckbox(field, passengerName));
      });

      const hiddenField = this.#hiddenFieldOfPassenger(field.dataset.id, field.id);
      this.formFilterTarget.append(hiddenField);

      return true;
    }

    #passengerNameByLine(line) {
      return find('td:nth-child(2)', line).textContent.trim();
    }

    #notifySelectedPassenger(event) {
      const passengerName = this.#passengerNameByLine(getParent(find(`#${event.target.id}`), 2));
      this.#notify(passengerName, 'Passageiro adicionado', 'info');
    }

    #notifyUnselectedPassenger(event) {
      const passengerName = this.#passengerNameByLine(getParent(find(`#${event.target.id}`), 2));
      this.#notify(passengerName, 'Passageiro removido', 'warn');
    }

    #notifyLimitMessage() {
      this.#notify("Número máximo de passageiros selecionados.\nPara adicionar mais, inicie uma nova solicitação.", null, 'warn');
    }

    #notify(message, title, kind = 'info') {
      const notify = this.application.getControllerForElementAndIdentifier(
        find('#routing-request-form'),
        'notifications'
      );
      notify.titleValue = title || '';
      notify.messageValue = message;
      if (kind == 'info') {
        notify.info();
      } else if (kind == 'warn') {
        notify.warn();
      }
    }

    #countSelectedPassengers() {
      return findAll('tbody tr', this.selectedPassengersBoxTarget).length;
    }

    #updateSelectedCounter() {
      this.selectedCounterTarget.innerText = this.#countSelectedPassengers();
    }

    #newCheckbox(oldField) {
      const newField = document.createElement('input');
      newField.type = 'checkbox';
      newField.value = oldField.value;
      newField.id = oldField.id;
      newField.name = 'routing_request[items][]';
      newField.checked = true;
      newField.addEventListener('change', this.#removeLineFromSelectedBoxAndReload.bind(this));
      return newField;
    }

    #hiddenFieldOfPassenger(passengerId, fieldId) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'excluded_passengers[]';
      input.value = passengerId;
      input.id = fieldId;
      return input;
    }

    #removeLineFromSelectedBoxAndReload(event) {
      this.#notifyUnselectedPassenger(event);
      this.#removeLineFromSelectedBox(event.target);
      this.#reloadEmployeesListAndUpdateCounter();
    }

    #removeLineFromSelectedBox(field) {
      const passengerData = JSON.parse(field.value);
      const checkboxes = findAll(`#${field.id}`, this.selectedPassengersBoxTarget);

      checkboxes.forEach(checkbox => {
        if(getParent(checkbox, 2).tagName === 'TR') {
          getParent(checkbox, 2).remove();
        } else {
          getParent(checkbox, 3).remove();
        }
      });

      find(`#hidden_passenger_${passengerData.passenger_id}`, this.formFilterTarget).remove();
    }

    #reloadEmployeesListAndUpdateCounter() {
      if(!this.hasBtnApplyFilterTarget) return;
      this.#addBlockDivOverList();
      this.btnApplyFilterTarget.click();
      this.#updateSelectedCounter();
    }

    #addBlockDivOverList() {
      const blockDiv = document.createElement('div');
      addClass(
        blockDiv,
        ['absolute', 'blockDiv', 'top-0', 'left-0', 'w-full', 'h-full', 'bg-white', 'bg-opacity-50', 'z-30']
      );
      getParent(this.selectedPassengersBoxTarget).prepend(blockDiv);
      getParent(this.passengersBoxTarget).prepend(blockDiv.cloneNode());
    }

    #removeBlockDivOverList() {
      const blockDivOverPassengers = find('.blockDiv', getParent(this.selectedPassengersBoxTarget));
      if (blockDivOverPassengers) blockDivOverPassengers.remove();
    }

    #addEventOnEmployees() {
      this.employeesCheckboxesTargets.forEach(checkbox => {
        checkbox.addEventListener('change', this.#moveLineToSelectedBoxAndReload.bind(this));
      });
    }

    #removeEventOnEmployees() {
      this.employeesCheckboxesTargets.forEach(checkbox => {
        checkbox.removeEventListener('change', this.#moveLineToSelectedBoxAndReload.bind(this));
      });
    }

    #addEventOnPassengers() {
      this.passengersCheckboxesTargets.forEach(checkbox => {
        checkbox.addEventListener('change', this.#removeLineFromSelectedBoxAndReload.bind(this));
      });
    }

    #removeEventOnPassengers() {
      this.passengersCheckboxesTargets.forEach(checkbox => {
        checkbox.removeEventListener('change', this.#removeLineFromSelectedBoxAndReload.bind(this));
      });
    }
  }
);
